import { useEffect, useState } from "react";
import { Ban, Loader2, RotateCcw, ShieldCheck, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg, fmtDate, fmtNum } from "@/lib/api";

const PROTECTED = "Kept for 5 years for EUDR records — mark Not required instead";
const WHY = [["sales", "sales"], ["stock", "stock"], ["PO lines", "PO"], ["DDS", "DDS"], ["customer pack", "pack"]];

function Breakdown({ by, testid }) {
  return <p className="mt-1 text-[11px] text-amber-800" data-testid={testid}>{WHY.map(([k, l]) => `${l} ${fmtNum(by?.[k] || 0)}`).join(" · ")}</p>;
}

/** Admin only: include protected materials, choose keep-history / archive-lines mode, warn about DDS. */
function ForceBlock({ chk, force, setForce, mode, setMode }) {
  if (!chk?.can_force || !(chk.protected > 0 || chk.protected_by?.length)) return null;
  return (
    <div className="rounded-lg border border-rose-200 bg-rose-50/60 p-3 text-sm" data-testid="force-delete-block">
      <label className="flex items-center gap-2 font-medium text-rose-800">
        <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} data-testid="force-delete-checkbox" />
        Include protected materials (force delete)
      </label>
      {force && (
        <div className="mt-2 space-y-1.5 text-xs text-slate-700">
          <label className="flex items-start gap-2"><input type="radio" name="fmode" checked={mode === "keep"} onChange={() => setMode("keep")} data-testid="force-mode-keep" />
            <span><b>Keep history</b> (default): remove the material and skip it on future SAP uploads. Sales, PO lines, stock rows and DDS records stay untouched.</span></label>
          <label className="flex items-start gap-2"><input type="radio" name="fmode" checked={mode === "archive"} onChange={() => setMode("archive")} data-testid="force-mode-archive" />
            <span><b>Also remove its sales, PO and stock lines</b>: archived first so they can be restored or audited. DDS and customer-pack records are never touched.</span></label>
          <p className="font-semibold text-rose-700" data-testid="force-delete-warning">Force delete removes materials kept for EUDR's 5-year record retention. It is audited and can be undone from the Skipped list.</p>
          {chk.on_dds > 0 && (
            <p className="rounded border border-amber-300 bg-amber-50 p-2 text-amber-900" data-testid="force-delete-dds-warning">
              {fmtNum(chk.on_dds)} on a DDS or customer pack: these DDS stay untouched{chk.dds_numbers?.length ? ` (${chk.dds_numbers.slice(0, 20).join(", ")}${chk.dds_numbers.length > 20 ? ` +${chk.dds_numbers.length - 20} more` : ""})` : ""}.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/** Single material+plant: checks retention first, then offers Delete or Mark not required. */
export function DeleteMaterialDialog({ rec, onClose, onDone }) {
  const [chk, setChk] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [force, setForce] = useState(false);
  const [mode, setMode] = useState("keep");
  const [text, setText] = useState("");
  useEffect(() => {
    setChk(null); setReason(""); setForce(false); setMode("keep"); setText("");
    if (rec) api.get(`/materials/${rec.id}/delete-check`).then((r) => setChk(r.data)).catch((e) => { toast.error(errMsg(e)); onClose(); });
  }, [rec]); // eslint-disable-line react-hooks/exhaustive-deps
  const act = (path, msg, extra = {}) => async () => {
    setBusy(true);
    try { await api.post(`/materials/${rec.id}/${path}`, { reason, ...extra }); toast.success(msg); onDone(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!rec} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="material-delete-dialog">
        <DialogHeader>
          <DialogTitle>Delete material <span className="font-mono">{rec?.material}</span> · plant {rec?.plant}</DialogTitle>
          <DialogDescription>Deleted materials are skipped on future SAP uploads (file and script). Sales lines, PO lines and stock are never deleted.</DialogDescription>
        </DialogHeader>
        {!chk ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : chk.deletable ? (
          <p className="flex items-center gap-2 text-sm text-emerald-700" data-testid="material-delete-ok"><ShieldCheck className="h-4 w-4" />No sales, stock, PO lines or DDS / pack records within 5 years. It can be deleted.</p>
        ) : (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" data-testid="material-delete-protected">
            <p className="font-semibold">{PROTECTED}</p>
            <p className="mt-1 text-xs">Has: {chk.protected_by.join(", ")}</p>
          </div>
        )}
        <ForceBlock chk={chk} force={force} setForce={setForce} mode={mode} setMode={setMode} />
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (optional, saved in the audit log)" data-testid="material-delete-reason" />
        {force && (
          <div>
            <p className="mb-1 text-sm">Type <span className="font-mono font-semibold">DELETE</span> to confirm</p>
            <Input value={text} onChange={(e) => setText(e.target.value)} data-testid="material-force-confirm-input" />
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          {chk && !chk.not_required && (
            <Button variant="outline" disabled={busy} onClick={act("not-required", "Marked Not required")} data-testid="material-mark-not-required-btn"><Ban className="mr-1.5 h-4 w-4" />Mark not required</Button>
          )}
          {chk?.deletable && (
            <Button variant="destructive" disabled={busy} onClick={act("delete", "Material deleted and added to the Skipped list")} data-testid="material-delete-confirm-btn">
              {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Trash2 className="mr-1.5 h-4 w-4" />}Delete
            </Button>
          )}
          {force && !chk?.deletable && (
            <Button variant="destructive" disabled={busy || text !== "DELETE"} onClick={act("delete", "Material force-deleted and added to the Skipped list", { force: true, mode, confirm: text })} data-testid="material-force-delete-btn">
              {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Trash2 className="mr-1.5 h-4 w-4" />}Force delete
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** "Delete all in view": every material matching the filters + search across all pages. */
export function BulkDeleteDialog({ open, filters, onClose, onDone }) {
  const [sum, setSum] = useState(null);
  const [text, setText] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [force, setForce] = useState(false);
  const [mode, setMode] = useState("keep");
  useEffect(() => {
    setSum(null); setText(""); setReason(""); setForce(false); setMode("keep");
    if (open) api.post("/materials/delete-check", filters).then((r) => setSum(r.data)).catch((e) => { toast.error(errMsg(e)); onClose(); });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const n = force ? sum?.total || 0 : sum?.deletable || 0;
  const go = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/materials/delete-bulk", { ...filters, confirm: text, reason, ...(force ? { force: true, mode } : {}) });
      const arch = data.archived && Object.keys(data.archived).length ? ` · archived ${Object.entries(data.archived).map(([k, v]) => `${fmtNum(v)} ${k.replace("_", " ")}`).join(", ")}` : "";
      toast.success(force ? `${fmtNum(data.deleted)} materials force-deleted${arch}` : `${fmtNum(data.deleted)} materials deleted · ${fmtNum(data.protected)} protected and kept`);
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl" data-testid="bulk-delete-dialog">
        <DialogHeader>
          <DialogTitle>Delete all materials in view</DialogTitle>
          <DialogDescription>Applies to every material matching the current filters and search, on all pages. Deleted codes are skipped on future SAP uploads.</DialogDescription>
        </DialogHeader>
        {!sum ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : (
          <div className="grid grid-cols-4 gap-3 text-sm">
            <div className="rounded-lg border p-3"><p className="text-xs text-slate-500">Matching</p><p className="text-xl font-semibold" data-testid="bulk-delete-total">{fmtNum(sum.total)}</p></div>
            <div className="rounded-lg border border-rose-200 bg-rose-50 p-3"><p className="text-xs text-rose-700">Can be deleted</p><p className="text-xl font-semibold text-rose-700" data-testid="bulk-delete-deletable">{fmtNum(sum.deletable)}</p></div>
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
              <p className="text-xs text-amber-800">Protected (sales, stock or PO lines in the last 5 years; DDS: {fmtNum(sum.protected_by?.DDS || 0)})</p>
              <p className="text-xl font-semibold text-amber-800" data-testid="bulk-delete-protected">{fmtNum(sum.protected)}</p>
              <Breakdown by={sum.protected_by} testid="bulk-delete-protected-breakdown" />
            </div>
            <div className="rounded-lg border p-3"><p className="text-xs text-slate-500">On a DDS</p><p className="text-xl font-semibold" data-testid="bulk-delete-on-dds">{fmtNum(sum.on_dds || 0)}</p></div>
          </div>
        )}
        {sum?.protected > 0 && !force && <p className="text-xs text-slate-500">Protected materials stay. Open one to mark it Not required instead.</p>}
        <ForceBlock chk={sum} force={force} setForce={setForce} mode={mode} setMode={setMode} />
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (optional, saved in the audit log)" data-testid="bulk-delete-reason" />
        <div>
          <p className="mb-1 text-sm">Type <span className="font-mono font-semibold">DELETE</span> to confirm</p>
          <Input value={text} onChange={(e) => setText(e.target.value)} data-testid="bulk-delete-confirm-input" />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="destructive" disabled={text !== "DELETE" || busy || !n} onClick={go} data-testid="bulk-delete-confirm-btn">
            {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Trash2 className="mr-1.5 h-4 w-4" />}Delete {fmtNum(n)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Skipped (deleted) and Not required materials, with restore. */
export function SkippedSheet({ open, onClose, onChanged, canEdit }) {
  const [kind, setKind] = useState("skipped");
  const [q, setQ] = useState("");
  const [d, setD] = useState(null);
  const load = () => api.get("/materials/skipped", { params: { kind, q, page_size: 200 } }).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { if (open) { setD(null); load(); } }, [open, kind, q]); // eslint-disable-line react-hooks/exhaustive-deps
  const restore = async (x, lines = false) => {
    try {
      const { data } = await api.post(kind === "skipped" ? `/materials/skipped/${x.id}/restore` : `/materials/${x.id}/restore-required`, { reason: "", lines });
      toast.success(kind === "skipped" ? (data.material_back ? `Restored${lines ? " with archived lines" : ""}: back in the catalogue` : "Restored: the next materials upload brings it back") : "Restored: back in counts");
      load(); onChanged();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-2xl" data-testid="skipped-sheet">
        <SheetHeader><SheetTitle>Skipped / Not required materials</SheetTitle></SheetHeader>
        <div className="mt-4 flex items-center gap-2">
          <Tabs value={kind} onValueChange={setKind}>
            <TabsList>
              <TabsTrigger value="skipped" data-testid="skipped-tab-skipped">Skipped (deleted)</TabsTrigger>
              <TabsTrigger value="not_required" data-testid="skipped-tab-not-required">Not required</TabsTrigger>
            </TabsList>
          </Tabs>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search material…" className="h-9 max-w-[220px]" data-testid="skipped-search" />
          <span className="ml-auto text-xs text-slate-500" data-testid="skipped-total">{d ? `${fmtNum(d.total)} materials` : ""}</span>
        </div>
        {!d ? <Loader2 className="mt-6 h-5 w-5 animate-spin text-slate-400" /> : (
          <table className="mt-4 w-full text-sm" data-testid="skipped-table">
            <thead><tr className="border-b text-left text-xs text-slate-500"><th className="py-2">Material</th><th>Plant</th><th>Description</th><th>By / when</th><th>Reason</th><th /></tr></thead>
            <tbody>
              {d.items.map((x) => (
                <tr key={x.id} className="border-b last:border-0" data-testid={`skipped-row-${x.material}-${x.plant}`}>
                  <td className="py-2 font-mono text-xs">{x.material}</td><td className="font-mono text-xs">{x.plant}</td>
                  <td className="max-w-[180px] truncate text-xs" title={x.description}>{x.description || "—"}</td>
                  <td className="text-xs text-slate-500">{x.by}<br />{fmtDate(x.at, true)}</td>
                  <td className="max-w-[140px] truncate text-xs" title={x.reason}>{x.reason || "—"}</td>
                  <td className="text-right">{canEdit && (
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="ghost" onClick={() => restore(x)} data-testid={`skipped-restore-${x.material}-${x.plant}`}><RotateCcw className="mr-1 h-3.5 w-3.5" />Restore</Button>
                      {x.archived_lines && <Button size="sm" variant="outline" onClick={() => restore(x, true)} data-testid={`skipped-restore-lines-${x.material}-${x.plant}`}>Restore archived lines</Button>}
                    </div>
                  )}</td>
                </tr>
              ))}
              {!d.items.length && <tr><td colSpan={6} className="py-6 text-center text-xs text-slate-400">Nothing here</td></tr>}
            </tbody>
          </table>
        )}
      </SheetContent>
    </Sheet>
  );
}
